import React from 'react';
import { Input } from '../ui/input';
import { TooltipLabel } from '../ui/TooltipLabel';
import { financialManagementTooltips } from '../../utils/financialTooltips';
import { Calculator, TrendingUp } from 'lucide-react';
import { getCurrencySymbol } from '../../constants/currencies';

const EquityCalculator = ({ form, setForm, currentPlan }) => {
  const currencySymbol = getCurrencySymbol(form.currency || currentPlan?.currency || 'USD');
  
  // Calculate common stock value
  const issuedShares = parseFloat(form.issued_shares) || 0;
  const parValue = parseFloat(form.par_value) || 0;
  const commonStock = issuedShares * parValue;
  
  // Calculate additional components
  const additionalPaidIn = parseFloat(form.additional_paid_in_capital) || 0;
  const retainedEarnings = parseFloat(form.retained_earnings) || 0;
  const treasuryStock = parseFloat(form.treasury_stock) || 0;
  
  // Total equity value
  const totalEquityValue = commonStock + additionalPaidIn + retainedEarnings - treasuryStock;
  
  // Share utilization
  const authorizedShares = parseFloat(form.authorized_shares) || 0;
  const utilizationPercent = authorizedShares > 0 ? (issuedShares / authorizedShares) * 100 : 0;
  
  return (
    <div className="space-y-6">
      {/* Authorized & Issued Shares */}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <TooltipLabel htmlFor="authorized-shares" tooltip="Maximum shares company can issue per charter">
            Authorized Shares *
          </TooltipLabel>
          <Input 
            id="authorized-shares" 
            type="number" 
            value={form.authorized_shares}
            onChange={(e) => setForm({...form, authorized_shares: e.target.value})}
            placeholder="e.g., 1000000"
            required 
          />
        </div>
        <div>
          <TooltipLabel htmlFor="issued-shares" tooltip="Shares actually issued to shareholders">
            Issued Shares *
          </TooltipLabel>
          <Input 
            id="issued-shares" 
            type="number" 
            value={form.issued_shares}
            onChange={(e) => setForm({...form, issued_shares: e.target.value})}
            placeholder="e.g., 500000"
            required 
          />
          {authorizedShares > 0 && issuedShares > 0 && (
            <p className="text-xs text-gray-500 mt-1">
              {utilizationPercent.toFixed(1)}% of authorized shares issued
            </p>
          )}
          {issuedShares > authorizedShares && (
            <p className="text-xs text-red-600 mt-1">
              ⚠️ Issued shares exceed authorized shares!
            </p>
          )}
        </div>
      </div>

      {/* Par Value */}
      <div>
        <TooltipLabel htmlFor="par-value" tooltip="Nominal/face value per share (legal minimum)">
          Par Value *
        </TooltipLabel>
        <div className="relative">
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 text-sm">
            {currencySymbol}
          </span>
          <Input 
            id="par-value" 
            type="number" 
            step="0.01"
            value={form.par_value}
            onChange={(e) => setForm({...form, par_value: e.target.value})}
            placeholder="e.g., 10.00"
            className="pl-8"
            required 
          />
        </div>
      </div>

      {/* Auto-Calculated Common Stock Value */}
      <div className="bg-blue-50 border-2 border-blue-300 rounded-lg p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <Calculator className="h-5 w-5 text-blue-600" />
            <span className="font-medium text-blue-900">Common Stock Value</span>
          </div>
          <div className="text-right">
            <p className="text-2xl font-bold text-blue-900">
              {currencySymbol}{commonStock.toLocaleString(undefined, {minimumFractionDigits: 2})}
            </p>
            <p className="text-xs text-blue-600 mt-1">
              {issuedShares > 0 && parValue > 0 
                ? `${issuedShares.toLocaleString()} shares × ${currencySymbol}${parValue.toFixed(2)}`
                : 'Enter issued shares and par value'
              }
            </p>
          </div>
        </div>
      </div>

      {/* Additional Equity Components */}
      <div className="space-y-4">
        <h4 className="font-medium text-gray-900 flex items-center">
          <TrendingUp className="h-4 w-4 mr-2" />
          Additional Equity Components
        </h4>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <TooltipLabel htmlFor="additional-paid-in" tooltip="Amount investors paid above par value">
              Additional Paid-in Capital
            </TooltipLabel>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 text-sm">
                {currencySymbol}
              </span>
              <Input 
                id="additional-paid-in"
                type="number" 
                value={form.additional_paid_in_capital}
                onChange={(e) => setForm({...form, additional_paid_in_capital: e.target.value})}
                placeholder="0"
                className="pl-8"
              />
            </div>
            <p className="text-xs text-gray-500 mt-1">Premium over par value</p>
          </div>
          <div>
            <TooltipLabel htmlFor="retained-earnings" tooltip="Cumulative profits retained in the business">
              Retained Earnings
            </TooltipLabel>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 text-sm">
                {currencySymbol}
              </span>
              <Input 
                id="retained-earnings"
                type="number" 
                value={form.retained_earnings}
                onChange={(e) => setForm({...form, retained_earnings: e.target.value})}
                placeholder="0"
                className="pl-8"
              />
            </div>
            <p className="text-xs text-gray-500 mt-1">Accumulated profits</p>
          </div>
          <div>
            <TooltipLabel htmlFor="treasury-stock" tooltip="Company's own shares repurchased from market">
              Treasury Stock
            </TooltipLabel>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 text-sm">
                {currencySymbol}
              </span>
              <Input 
                id="treasury-stock"
                type="number" 
                value={form.treasury_stock}
                onChange={(e) => setForm({...form, treasury_stock: e.target.value})}
                placeholder="0"
                className="pl-8"
              />
            </div>
            <p className="text-xs text-gray-500 mt-1">Reduces total equity</p>
          </div>
          <div>
            <TooltipLabel htmlFor="cost-of-equity" tooltip="Required return for equity investors (CAPM or estimated)">
              Cost of Equity (%)
            </TooltipLabel>
            <Input 
              id="cost-of-equity"
              type="number" 
              step="0.1"
              value={form.cost_of_equity}
              onChange={(e) => setForm({...form, cost_of_equity: e.target.value})}
              placeholder="e.g., 12"
            />
            <p className="text-xs text-gray-500 mt-1">For WACC calculation</p>
          </div>
        </div>
      </div>

      {/* Total Equity Value Summary */}
      <div className="bg-green-50 border-2 border-green-300 rounded-lg p-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-green-700 font-medium">Total Shareholders' Equity</p>
            <p className="text-xs text-green-600 mt-1">
              Common Stock + APIC + Retained Earnings - Treasury Stock
            </p>
          </div>
          <div className="text-right">
            <p className="text-3xl font-bold text-green-900">
              {currencySymbol}{totalEquityValue.toLocaleString(undefined, {minimumFractionDigits: 2})}
            </p>
          </div>
        </div>
        
        {/* Breakdown */}
        {(commonStock > 0 || additionalPaidIn > 0 || retainedEarnings !== 0 || treasuryStock > 0) && (
          <div className="mt-3 pt-3 border-t border-green-200 grid grid-cols-4 gap-2 text-xs">
            <div>
              <p className="text-green-600">Common Stock</p>
              <p className="font-medium text-green-900">{currencySymbol}{commonStock.toLocaleString()}</p>
            </div>
            {additionalPaidIn > 0 && (
              <div>
                <p className="text-green-600">+ APIC</p>
                <p className="font-medium text-green-900">{currencySymbol}{additionalPaidIn.toLocaleString()}</p>
              </div>
            )}
            {retainedEarnings !== 0 && (
              <div>
                <p className="text-green-600">{retainedEarnings > 0 ? '+ Retained' : '- Deficit'}</p>
                <p className="font-medium text-green-900">{currencySymbol}{Math.abs(retainedEarnings).toLocaleString()}</p>
              </div>
            )}
            {treasuryStock > 0 && (
              <div>
                <p className="text-green-600">- Treasury</p>
                <p className="font-medium text-green-900">{currencySymbol}{treasuryStock.toLocaleString()}</p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Validation Warnings */}
      {issuedShares > authorizedShares && (
        <div className="bg-red-50 border border-red-300 rounded p-3">
          <p className="text-sm text-red-800">
            ⚠️ <strong>Error:</strong> Issued shares ({issuedShares.toLocaleString()}) exceed authorized shares ({authorizedShares.toLocaleString()})
          </p>
        </div>
      )}
    </div>
  );
};

export default EquityCalculator;
